import type { MutableRefObject } from 'react'
import { postKnowledgeBase } from '../api/voice-bot-client'
import {
  collectTravelContextHints,
  enrichTravelSearchQuery,
  extractTravelDestinationHints,
} from '../utils/travel-hints'
import { formatKbForBot } from './kb-result-formatters'
import {
  FUNCTION_OUTPUT_LANGUAGE_WRAPPER,
  type LanguageCode,
} from '../config/language'
import type { Message, VoiceSessionData } from '../types'

// ============================================
// runKnowledgeBaseSearch — search_knowledge_base tool handler
// ============================================
// Lifted from VoiceBotPanel.handleFunctionCall as part of Wave 3b. The
// handler is fully ref-driven (no React state), so it extracts cleanly:
//
//   - Reads inputs from refs (session industry/customerId, recent
//     transcript, last user message, prior travel context hints,
//     selected language).
//   - Performs industry-specific query rewriting (travel enrichment,
//     insurance filler stripping) and hits the knowledge-base API.
//   - For travel: applies an "abroad" filter, reranks by destination
//     match, and runs a simplified retry when the top result misses
//     the geographic intent.
//   - For insurance: retries once with conversational filler stripped
//     when the first search returns nothing.
//   - Updates pendingLinksRef when the formatter surfaces an article
//     link, and updates lastTravelContextHintsRef from new results.
//   - Returns the formatted string the assistant model should consume
//     (caller wraps it in the function_call_output frame).
//
// Behavior is intentionally preserved 1:1 with the original inline
// handler.

interface KbResult {
  id?: string
  title?: string
  content?: string
  metadata?: {
    country_he?: string
    country_en?: string
    [k: string]: unknown
  }
  [k: string]: unknown
}

interface KbResponseBody {
  results?: KbResult[]
  count?: number
  [k: string]: unknown
}

export interface RunKnowledgeBaseSearchOptions {
  query: unknown
  publicKey?: string
  sessionDataRef: MutableRefObject<VoiceSessionData | null>
  transcriptMessagesRef: MutableRefObject<Message[]>
  lastUserMessageRef: MutableRefObject<string>
  lastTravelContextHintsRef: MutableRefObject<string[]>
  pendingLinksRef: MutableRefObject<Array<{ label: string; url: string }> | null>
  selectedLanguageRef: MutableRefObject<LanguageCode>
  /**
   * Residents (Dona) VOICE-only: the caller-confirmed ת.ז + apartment, forwarded
   * so the server can re-resolve the renter's contract on this call. Untrusted
   * hint — the server HMACs the ת.ז and fails closed if it doesn't match exactly
   * one approved contract. Omitted entirely for non-residents / general queries.
   */
  residentVerify?: { tz: string; apartment: string }
}

const ABROAD_PATTERN = /חו"ל|חו״ל|חול|חוץ לארץ|abroad|overseas/i

const INTENT_MATCHERS: Record<string, RegExp> = {
  food: /(כשר|מסעד|אוכל|restaurant|food|kosher|dining|eat)/i,
  hotel: /(מלון|hotel|booking|stay|sleep|לינה|resort)/i,
  car: /(רכב|השכרת|car|rental|drive)/i,
  tour: /(סיור|אטרקצי|פעילו|tour|activity|activities|attraction)/i,
  flight: /(טיס|flight|airfare|plane)/i,
}

const INTENT_TERM_MAP: Record<string, string> = {
  hotel: 'מלונות',
  food: 'מסעדות',
  car: 'השכרת רכב',
  tour: 'אטרקציות',
  flight: 'טיסות',
}

const INSURANCE_FILLER_PREFIX =
  /^(כן|אוקיי|אוקי|מצוין|מעולה|תודה|תודה רבה|יש לי עוד שאלה|עוד שאלה)[,!.?\-\s]*/u

export const runKnowledgeBaseSearch = async (
  opts: RunKnowledgeBaseSearchOptions
): Promise<string> => {
  const {
    query,
    publicKey,
    sessionDataRef,
    transcriptMessagesRef,
    lastUserMessageRef,
    lastTravelContextHintsRef,
    pendingLinksRef,
    selectedLanguageRef,
    residentVerify,
  } = opts

  console.log('🔍 BOT SEARCHING KNOWLEDGE BASE:', query)
  const customerIndustry = sessionDataRef.current?.industry || 'real_estate'
  const originalQuery = String(query || '').trim()
  const effectiveQuery =
    customerIndustry === 'travel'
      ? enrichTravelSearchQuery(
          originalQuery,
          transcriptMessagesRef.current,
          lastUserMessageRef.current,
          lastTravelContextHintsRef.current
        )
      : originalQuery

  if (customerIndustry === 'travel' && effectiveQuery !== originalQuery) {
    console.log('🧭 Enriched travel KB query with recent destination context:', {
      originalQuery,
      effectiveQuery,
    })
  }

  const kbResponse = await postKnowledgeBase(
    {
      query: effectiveQuery,
      customerId: sessionDataRef.current?.customerId,
      ...(residentVerify ? { residentVerify } : {}),
    },
    publicKey
  )
  let kbData: KbResponseBody = await kbResponse.json()

  if (customerIndustry === 'travel') {
    if (Array.isArray(kbData?.results) && kbData.results.length > 0) {
      // "Abroad" filter: if query says "חו"ל" / "חוץ לארץ" / "abroad",
      // exclude Israel results.
      if (ABROAD_PATTERN.test(effectiveQuery)) {
        const nonIsrael = kbData.results.filter((r) => {
          const country = `${r.metadata?.country_he || ''} ${
            r.metadata?.country_en || ''
          }`.toLowerCase()
          return !/ישראל|israel/.test(country)
        })
        if (nonIsrael.length > 0) {
          kbData.results = nonIsrael
          console.log(
            '✈️ Abroad filter: removed Israel results, kept:',
            nonIsrael.map((r) => r.title)
          )
        }
      }

      // Rerank: push geographically matching articles to top.
      const queryDestHints = extractTravelDestinationHints(effectiveQuery)
      if (queryDestHints.length > 0 && kbData.results.length > 1) {
        kbData.results.sort((a, b) => {
          const aGeo = extractTravelDestinationHints(
            `${a.metadata?.country_he || ''} ${a.metadata?.country_en || ''} ${a.title || ''}`
          )
          const bGeo = extractTravelDestinationHints(
            `${b.metadata?.country_he || ''} ${b.metadata?.country_en || ''} ${b.title || ''}`
          )
          const aMatch = queryDestHints.some((h) => aGeo.includes(h)) ? 1 : 0
          const bMatch = queryDestHints.some((h) => bGeo.includes(h)) ? 1 : 0
          return bMatch - aMatch
        })
        console.log(
          '🌍 Travel results reranked by destination match:',
          kbData.results.map((r) => r.title)
        )

        // Travel retry: if no geo-matched result in top, retry with a
        // simplified "{intent term} {destination}" query and merge.
        const top = kbData.results[0]
        const topGeo = extractTravelDestinationHints(
          `${top?.metadata?.country_he || ''} ${top?.metadata?.country_en || ''} ${
            top?.title || ''
          }`
        )
        const topIsGeoMatch = queryDestHints.some((h) => topGeo.includes(h))
        if (!topIsGeoMatch) {
          const detectedIntent =
            (Object.entries(INTENT_MATCHERS).find(([, m]) =>
              m.test(effectiveQuery)
            )?.[0] as string | undefined) || 'general'
          const intentTerm = INTENT_TERM_MAP[detectedIntent] || ''
          const destTerm = queryDestHints[0] || ''
          if (destTerm) {
            const simplifiedQuery = `${intentTerm} ${destTerm}`.trim()
            console.log('🔁 Travel retry with simplified query:', simplifiedQuery)
            const retryResponse = await postKnowledgeBase(
              {
                query: simplifiedQuery,
                customerId: sessionDataRef.current?.customerId,
              },
              publicKey
            )
            const retryData: KbResponseBody = await retryResponse.json()
            if (retryData?.results && retryData.results.length > 0) {
              const existingIds = new Set(
                kbData.results.map((r) => r.id).filter(Boolean)
              )
              const newResults = retryData.results.filter(
                (r) => !existingIds.has(r.id)
              )
              if (newResults.length > 0) {
                const geoMatchedRetry = newResults.filter((r) => {
                  const rGeo = extractTravelDestinationHints(
                    `${r.metadata?.country_he || ''} ${
                      r.metadata?.country_en || ''
                    } ${r.title || ''}`
                  )
                  return queryDestHints.some((h) => rGeo.includes(h))
                })
                kbData.results = [...geoMatchedRetry, ...kbData.results]
                console.log(
                  '🔁 Travel retry added',
                  geoMatchedRetry.length,
                  'geo-matched results:',
                  geoMatchedRetry.map((r) => r.title)
                )
              }
            }
          }
        }
      }

      const nextContextHints = collectTravelContextHints(
        kbData.results,
        effectiveQuery
      )
      if (nextContextHints.length > 0) {
        lastTravelContextHintsRef.current = nextContextHints
      }
    } else {
      const explicitQueryHints = extractTravelDestinationHints(effectiveQuery)
      if (explicitQueryHints.length > 0) {
        lastTravelContextHintsRef.current = explicitQueryHints
      }
    }
  }

  // Insurance-only retry: strip conversational filler and search once more.
  if (
    customerIndustry === 'insurance' &&
    (!kbData.results || kbData.results.length === 0)
  ) {
    const normalizedQuery = originalQuery.replace(INSURANCE_FILLER_PREFIX, '').trim()
    if (normalizedQuery && normalizedQuery !== originalQuery) {
      console.log('🔁 Insurance KB retry with normalized query:', normalizedQuery)
      const retryResponse = await postKnowledgeBase(
        {
          query: normalizedQuery,
          customerId: sessionDataRef.current?.customerId,
        },
        publicKey
      )
      const retryData: KbResponseBody = await retryResponse.json()
      if (retryData?.results && retryData.results.length > 0) {
        kbData = retryData
      }
    }
  }

  console.log(
    `✅ Knowledge Base Search Results: ${kbData.count || 0} properties found`
  )
  if (kbData.results && kbData.results.length > 0) {
    console.log('📝 Results:', kbData.results.map((r) => r.title))
    console.log(
      '📄 Full data being sent to bot:',
      kbData.results
        .map((r) => r.content)
        .join('\n\n')
        .substring(0, 500) + '...'
    )
  } else {
    console.warn('⚠️ No results found in knowledge base for query:', query)
  }

  // Format KB results for the assistant model (pure transformation).
  // Side effects (pendingLinksRef for travel article links) applied here.
  const formatted = formatKbForBot(
    kbData,
    effectiveQuery,
    customerIndustry,
    selectedLanguageRef.current
  )

  if (formatted.articleLink) {
    pendingLinksRef.current = [
      { label: formatted.articleLink.label, url: formatted.articleLink.url },
    ]
    console.log('🔗 Article link for next message:', formatted.articleLink.label)
  }

  if (kbData.results && kbData.results.length > 0) {
    if (customerIndustry === 'insurance') {
      console.log('📤 Insurance KB instruction applied for function output')
    } else if (customerIndustry === 'travel') {
      console.log('📤 Travel KB instruction with affiliate links applied')
    } else {
      const langWrapper =
        FUNCTION_OUTPUT_LANGUAGE_WRAPPER[selectedLanguageRef.current] || {
          prefix: '',
          suffix: '',
        }
      console.log(
        '📤 Language-wrapped result prefix:',
        langWrapper.prefix ? 'Applied' : 'None (Hebrew)'
      )
    }
  }

  return formatted.output
}
